import React, { useState, useCallback, useRef } from 'react';
import { Button, Modal, Stack, Flex, spacing, useTheme } from '@fti/design-system';
import { SUPPORTED_EXTENSIONS, MAX_FILE_SIZE, detectFileFormat } from '../../services/datasetApi';
import type { ExistingDatasetConflict } from '../../types/dataset';

interface UploadZoneProps {
  onFileSelected: (file: File) => void;
  conflict: ExistingDatasetConflict | null;
  onConfirmReplace: () => void;
  onCancelReplace: () => void;
  loadedFilename?: string;
}

const ACCEPT_STRING = '.csv,.xlsx,.json,.parquet,.pq';

export const UploadZone: React.FC<UploadZoneProps> = ({
  onFileSelected,
  conflict,
  onConfirmReplace,
  onCancelReplace,
  loadedFilename,
}) => {
  const { theme } = useTheme();
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string | null => {
    const format = detectFileFormat(file.name);
    if (!format) {
      return `Unsupported file format. Please upload a ${SUPPORTED_EXTENSIONS.join(', ')} file.`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File size (${(file.size / 1024 / 1024 / 1024).toFixed(2)} GB) exceeds the 1 GB limit.`;
    }
    if (file.size === 0) {
      return 'The uploaded file contains no data.';
    }
    return null;
  }, []);

  const processFile = useCallback((file: File) => {
    const error = validateFile(file);
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    onFileSelected(file);
  }, [validateFile, onFileSelected]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFile(files[0]);
    }
  }, [processFile]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [processFile]);

  const handleButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  }, []);

  const borderColor = isDragOver
    ? theme.colors.primary[500]
    : theme.colors.border.default;

  const bgColor = isDragOver
    ? `${theme.colors.primary[500]}08`
    : theme.colors.background.paper;

  return (
    <>
      {loadedFilename && (
        <div
          style={{
            padding: spacing.md,
            borderRadius: '8px',
            backgroundColor: `${theme.colors.semantic.success.light}10`,
            border: `1px solid ${theme.colors.semantic.success.light}`,
            marginBottom: spacing.md,
          }}
        >
          <Flex justify="between" align="center">
            <Flex gap="sm" align="center">
              <span style={{ fontSize: '1.1rem' }}>📄</span>
              <span style={{ color: theme.colors.text.primary, fontWeight: 500 }}>
                {loadedFilename}
              </span>
              <span style={{ color: theme.colors.semantic.success.default, fontSize: '0.85rem' }}>
                Loaded
              </span>
            </Flex>
            <span style={{ color: theme.colors.text.secondary, fontSize: '0.85rem' }}>
              Drop or choose a new file below to replace
            </span>
          </Flex>
        </div>
      )}

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label="Upload file drop zone"
        style={{
          border: `2px dashed ${borderColor}`,
          borderRadius: '8px',
          padding: spacing.xl,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'border-color 0.2s, background-color 0.2s',
          backgroundColor: bgColor,
        }}
      >
        <Stack gap="md" style={{ alignItems: 'center' }}>
          <p
            style={{
              margin: 0,
              fontSize: '1rem',
              color: theme.colors.text.secondary,
            }}
          >
            {isDragOver
              ? 'Drop your file here'
              : loadedFilename
                ? 'Drop a new file here to replace, or click to browse'
                : 'Drag and drop a file here, or click to browse'}
          </p>

          <Button
            variant="solid"
            color="primary"
            size="md"
            onClick={handleButtonClick}
          >
            {loadedFilename ? 'Replace file' : 'Choose file'}
          </Button>

          <p
            style={{
              margin: 0,
              fontSize: '0.85rem',
              color: theme.colors.text.disabled,
            }}
          >
            Supported formats: CSV, XLSX, JSON, Parquet (max 1 GB)
          </p>
        </Stack>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_STRING}
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          aria-hidden="true"
        />
      </div>

      {validationError && (
        <div
          style={{
            marginTop: spacing.sm,
            padding: spacing.sm,
            borderRadius: '6px',
            backgroundColor: `${theme.colors.semantic.error.light}20`,
            border: `1px solid ${theme.colors.semantic.error.light}`,
            color: theme.colors.semantic.error.default,
            fontSize: '0.9rem',
          }}
        >
          {validationError}
        </div>
      )}

      {conflict && (
        <Modal isOpen={true} onClose={onCancelReplace}>
          <Modal.Header onClose={onCancelReplace}>
            <h2 style={{ margin: 0, fontSize: '1.1rem', color: theme.colors.text.primary }}>
              Replace existing dataset?
            </h2>
          </Modal.Header>
          <Modal.Body>
            <Stack gap="md">
              <p style={{ margin: 0, color: theme.colors.text.primary }}>
                This workspace already has a dataset: <strong>{conflict.existing_dataset.filename}</strong>{' '}
                ({conflict.existing_dataset.row_count.toLocaleString()} rows).
              </p>
              <p style={{ margin: 0, color: theme.colors.text.secondary }}>
                Uploading a new file will permanently replace the existing dataset.
              </p>
            </Stack>
          </Modal.Body>
          <Modal.Footer>
            <Flex gap="sm" justify="end">
              <Button variant="outline" color="neutral" size="sm" onClick={onCancelReplace}>
                Cancel
              </Button>
              <Button
                variant="solid"
                color="error"
                size="sm"
                onClick={onConfirmReplace}
              >
                Replace dataset
              </Button>
            </Flex>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};
